package ru.chm.gradereport.platform

import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.Document
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.events.Event

actual fun getApiBaseUrl(): String {
    val configured = window.asDynamic().__GRADE_API_BASE__ as? String
    if (configured != null) {
        val trimmed = configured.trim().trimEnd('/')
        // Empty string = same origin (production: UI + API on one host).
        return trimmed.ifEmpty { window.location.origin }
    }

    val protocol = window.location.protocol.ifBlank { "http:" }
    val hostname = window.location.hostname.ifBlank { "localhost" }
    val port = window.location.port
    // Same-origin production (Docker / installDist serving static UI on 8085).
    if (port.isBlank() || port == "8085" || port == "80" || port == "443") {
        return window.location.origin
    }
    // Local Compose webpack (usually 8086) → API on 8085.
    return "$protocol//$hostname:8085"
}

actual fun loadAuthToken(): String? = localStorage.getItem("grade_auth_token")

actual fun saveAuthToken(token: String?) {
    if (token == null) localStorage.removeItem("grade_auth_token")
    else localStorage.setItem("grade_auth_token", token)
}

actual fun loadThemeMode(): String? = localStorage.getItem("grade_theme_mode")

actual fun saveThemeMode(mode: String) {
    localStorage.setItem("grade_theme_mode", mode)
}

actual fun isSystemInDarkTheme(): Boolean =
    window.matchMedia("(prefers-color-scheme: dark)").matches

actual fun addSystemThemeListener(listener: (isDark: Boolean) -> Unit): () -> Unit {
    val media = window.matchMedia("(prefers-color-scheme: dark)")
    val handler: (Event) -> Unit = {
        listener(media.matches)
    }
    // Modern browsers
    media.addEventListener("change", handler)
    return {
        media.removeEventListener("change", handler)
    }
}

actual fun currentDateIso(): String {
    val d = kotlin.js.Date()
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

actual fun currentDateTimeIso(): String {
    val d = kotlin.js.Date()
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    val h = d.getHours().toString().padStart(2, '0')
    val min = d.getMinutes().toString().padStart(2, '0')
    return "$y-$m-$day $h:$min"
}

actual fun getAppHashPath(): String {
    val hash = window.location.hash.removePrefix("#")
    val normalized = "/" + hash.trim('/')
    return if (normalized == "/") "" else normalized
}

actual fun setAppHashPath(path: String) {
    val normalized = when {
        path.isBlank() || path == "/" -> "/home"
        path.startsWith("/") -> path
        else -> "/$path"
    }
    val target = "#$normalized"
    if (window.location.hash != target) {
        // pushState keeps browser history; does not fire hashchange (avoids loops).
        window.history.pushState(null, "", window.location.pathname + window.location.search + target)
    }
}

actual fun addAppHashPathListener(listener: (String) -> Unit): () -> Unit {
    val handler: (Event) -> Unit = { listener(getAppHashPath()) }
    window.addEventListener("hashchange", handler)
    window.addEventListener("popstate", handler)
    return {
        window.removeEventListener("hashchange", handler)
        window.removeEventListener("popstate", handler)
    }
}

actual fun currentPageBaseUrl(): String {
    val origin = window.location.origin.ifBlank { "http://localhost" }
    val path = window.location.pathname.ifBlank { "/" }
    return origin + path
}

actual fun currentEpochMs(): Long = kotlin.js.Date().getTime().toLong()

actual fun copyTextToClipboard(text: String) {
    window.navigator.clipboard.writeText(text)
}

actual fun applyDocumentChrome(darkTheme: Boolean) {
    val bg = if (darkTheme) "#111827" else "#f1f5f9"
    val scheme = if (darkTheme) "dark" else "light"
    val doc = window.document
    val root = doc.documentElement.unsafeCast<org.w3c.dom.HTMLElement>()
    root.style.setProperty("background-color", bg)
    root.style.setProperty("color-scheme", scheme)
    doc.body?.style?.setProperty("background-color", bg)
    doc.body?.style?.setProperty("color-scheme", scheme)

    val head = doc.head ?: return
    val old = head.querySelectorAll("meta[name='theme-color']")
    val toRemove = mutableListOf<org.w3c.dom.Node>()
    for (i in 0 until old.length) {
        old.item(i)?.let { toRemove.add(it) }
    }
    toRemove.forEach { it.parentNode?.removeChild(it) }
    val meta = doc.createElement("meta")
    meta.setAttribute("name", "theme-color")
    meta.setAttribute("content", bg)
    head.appendChild(meta)
}

actual fun openUrl(url: String) {
    val trimmed = url.trim()
    if (trimmed.isEmpty()) return
    window.open(trimmed, "_blank")
}

private const val QR_SCALE = 6
private const val QR_MARGIN_PX = 64

actual val supportsQrPngDownload: Boolean = true

actual fun downloadQrPngHighRes(fileName: String, pngBase64: String) {
    val data = pngBase64.trim()
    if (data.isEmpty()) return
    val doc = window.document

    val source = doc.createElement("img").unsafeCast<HTMLImageElement>()
    source.onload = { saveQrPngHighRes(doc, source, fileName) }
    source.addEventListener("error") { console.error("QR hi-res download failed: PNG decode error") }
    source.src = "data:image/png;base64,$data"
}

/**
 * Upscales the source PNG (nearest-neighbour keeps QR modules pixel-perfect)
 * onto a white background with a quiet zone and triggers a browser download.
 */
private fun saveQrPngHighRes(doc: Document, source: HTMLImageElement, fileName: String) {
    try {
        val srcW = source.naturalWidth.takeIf { it > 0 } ?: 320
        val srcH = source.naturalHeight.takeIf { it > 0 } ?: 320

        // 1) Decode the source PNG into a canvas.
        val srcCanvas = doc.createElement("canvas").unsafeCast<HTMLCanvasElement>()
        srcCanvas.width = srcW
        srcCanvas.height = srcH
        val srcCtx = srcCanvas.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
        srcCtx.drawImage(source, 0.0, 0.0)

        // 2) Upscale x6 with smoothing disabled on a white background.
        val totalW = srcW * QR_SCALE + QR_MARGIN_PX * 2
        val totalH = srcH * QR_SCALE + QR_MARGIN_PX * 2
        val out = doc.createElement("canvas").unsafeCast<HTMLCanvasElement>()
        out.width = totalW
        out.height = totalH
        val ctx = out.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
        ctx.fillStyle = "#FFFFFF"
        ctx.fillRect(0.0, 0.0, totalW.toDouble(), totalH.toDouble())
        ctx.imageSmoothingEnabled = false
        ctx.drawImage(
            srcCanvas,
            QR_MARGIN_PX.toDouble(),
            QR_MARGIN_PX.toDouble(),
            (srcW * QR_SCALE).toDouble(),
            (srcH * QR_SCALE).toDouble(),
        )

        // 3) Trigger the browser download.
        val link = doc.createElement("a").unsafeCast<HTMLAnchorElement>()
        link.href = out.asDynamic().toDataURL("image/png") as String
        link.download = fileName
        doc.body?.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
    } catch (t: Throwable) {
        console.error("QR hi-res download failed", t)
    }
}
